import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, Megaphone, Image, ShieldCheck } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { isSupabaseConfigured } from "@/lib/supabase";
import { listPages } from "@/lib/cms/pages";
import { listAnnouncements } from "@/lib/cms/announcements";
import { listMedia } from "@/lib/cms/media";

export default function CmsDashboard() {
  const { profile } = useAuth();
  const [counts, setCounts] = useState(null);

  useEffect(() => {
    if (!isSupabaseConfigured || !profile?.organization_id) {
      setCounts({ pages: 0, announcements: 0, media: 0 });
      return undefined;
    }
    let alive = true;
    (async () => {
      try {
        const [pages, announcements, media] = await Promise.all([
          listPages(),
          listAnnouncements(),
          listMedia(profile.organization_id),
        ]);
        if (alive) setCounts({ pages: pages.length, announcements: announcements.length, media: media.length });
      } catch {
        // The collection pages remain the source of truth if a count query is
        // unavailable; don't turn the CMS home into a blocking error state.
        if (alive) setCounts(null);
      }
    })();
    return () => { alive = false; };
  }, [profile?.organization_id]);

  return (
    <div data-testid="cms-dashboard" className="space-y-8">
      <section>
        <p className="overline text-primary-hover">Content</p>
        <h1 className="mt-2 font-heading text-3xl font-bold tracking-tight text-neutral-900 sm:text-4xl">
          CMS workspace
        </h1>
        <p className="mt-2 max-w-2xl text-neutral-600">
          Manage public pages, announcements, and media assets. Operational
          data — child medical records, billing, and incidents — is intentionally
          out of scope for this workspace.
        </p>
      </section>

      <section
        data-testid="cms-boundary-notice"
        className="flex items-start gap-3 rounded-2xl border border-primary/30 bg-primary/5 p-4"
      >
        <ShieldCheck size={20} weight="duotone" className="mt-0.5 text-primary-hover" />
        <div className="text-sm leading-relaxed text-neutral-800">
          <p className="font-semibold text-neutral-900">Content-only boundary</p>
          <p className="mt-1 text-neutral-700">
            Your role is scoped to content collections. You will not see
            children's medical, attendance, or billing data.
          </p>
        </div>
      </section>

      <section className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <CmsCollectionCard icon={FileText} label="Pages" count={counts ? counts.pages : "…"} to="/cms/pages" testId="cms-collection-pages" />
        <CmsCollectionCard icon={Megaphone} label="Announcements" count={counts ? counts.announcements : "…"} to="/cms/announcements" testId="cms-collection-announcements" />
        <CmsCollectionCard icon={Image} label="Media assets" count={counts ? counts.media : "…"} to="/cms/media" testId="cms-collection-media" />
      </section>

      <section className="rounded-2xl border border-primary/25 bg-primary/5 p-5">
        <p className="font-heading text-base font-semibold text-neutral-900">Publishing tools are ready</p>
        <p className="mt-1 max-w-2xl text-sm leading-relaxed text-neutral-700">
          Create pages, format content with the Markdown editor, publish announcements, and manage the media library from the links above.
        </p>
      </section>
    </div>
  );
}

function CmsCollectionCard({ icon: Icon, label, count, to, testId }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className="block rounded-2xl border border-neutral-200 bg-white p-6 shadow-card transition-all hover:-translate-y-[2px] hover:border-primary/40 hover:shadow-raised"
    >
      <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10">
        <Icon size={22} weight="duotone" className="text-primary-hover" />
      </div>
      <p className="overline mt-6 text-neutral-500">{label}</p>
      <p className="tnum mt-1.5 font-heading text-3xl font-bold tracking-tight text-neutral-900">{count}</p>
      <p className="mt-2 text-xs font-semibold text-primary-hover">Open collection →</p>
    </Link>
  );
}
